import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import apiurl from "../Api";
import RefreshContext from "./RefreshContext";

function Accounts() {
  const [activeTab, setActiveTab] = useState("approved");
  const [approvedRows, setApprovedRows] = useState([]);
  const [updateRows, setUpdateRows] = useState([]);
  const [loadingApproved, setLoadingApproved] = useState(false);
  const [loadingUpdates, setLoadingUpdates] = useState(false);
  const [formByInvoice, setFormByInvoice] = useState({});
  const [submittingByInvoice, setSubmittingByInvoice] = useState({});
  const [markingPaidByInvoice, setMarkingPaidByInvoice] = useState({});
  const [receiptUploadingByInvoice, setReceiptUploadingByInvoice] = useState({});
  const { refreshKey } = useContext(RefreshContext);

  const fetchApprovedInvoices = async () => {
    try {
      setLoadingApproved(true);
      const response = await axios.get(`${apiurl}accounts.php?mode=approved`);
      const rows = response.data?.status === true ? response.data.data || [] : [];
      setApprovedRows(rows);

      const nextForm = {};
      rows.forEach((item) => {
        nextForm[item.id] = {
          utr_no: item.utr_no || "",
          remark: item.remark || "",
        };
      });
      setFormByInvoice(nextForm);
    } catch (error) {
      setApprovedRows([]);
      setFormByInvoice({});
    } finally {
      setLoadingApproved(false);
    }
  };

  const fetchPaymentUpdates = async () => {
    try {
      setLoadingUpdates(true);
      const response = await axios.get(`${apiurl}accounts.php?mode=updates`);
      if (response.data?.status === true) {
        setUpdateRows(response.data.data || []);
      } else {
        setUpdateRows([]);
      }
    } catch (error) {
      setUpdateRows([]);
    } finally {
      setLoadingUpdates(false);
    }
  };

  useEffect(() => {
    fetchApprovedInvoices();
    fetchPaymentUpdates();
  }, [refreshKey]);

  const formatAmount = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "-";
    return `INR ${num.toLocaleString("en-IN")}`;
  };

  const parseAmountNumber = (value) => {
    const cleaned = (value || "").toString().replace(/[^0-9.-]/g, "");
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : NaN;
  };

  const getAccountsAmount = (item) => {
    const netAmount = parseAmountNumber(item?.net_payable_amount);
    if (Number.isFinite(netAmount) && netAmount >= 0) return netAmount;
    return NaN;
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString("en-GB");
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const openReceiptDocument = (fileName) => {
    const safeName = (fileName || "").toString().trim();
    if (!safeName) return;
    const safeUrl = `${apiurl}uploads/receipts/${encodeURIComponent(safeName)}`;
    window.open(safeUrl, "_blank", "noopener,noreferrer");
  };

  const updateFormField = (invoiceId, key, value) => {
    setFormByInvoice((prev) => ({
      ...prev,
      [invoiceId]: {
        utr_no: prev[invoiceId]?.utr_no || "",
        remark: prev[invoiceId]?.remark || "",
        [key]: value,
      },
    }));
  };

  const handleSubmitUpdate = async (row) => {
    const invoiceId = row.id;
    const current = formByInvoice[invoiceId] || { utr_no: "", remark: "" };
    const utrNo = (current.utr_no || "").trim();
    const remark = (current.remark || "").trim();

    if (!utrNo) {
      Swal.fire("Warning", "UTR No. is mandatory", "warning");
      return;
    }

    try {
      setSubmittingByInvoice((prev) => ({ ...prev, [invoiceId]: true }));
      const formData = new FormData();
      formData.append("action", "save_update");
      formData.append("invoice_id", String(invoiceId));
      formData.append("utr_no", utrNo);
      formData.append("remark", remark);

      const response = await axios.post(`${apiurl}accounts.php`, formData);
      if (response.data?.status === true) {
        Swal.fire("Success", response.data.message || "Saved", "success");
        await fetchApprovedInvoices();
        await fetchPaymentUpdates();
        setActiveTab("updates");
      } else {
        Swal.fire("Error", response.data?.message || "Server Error", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Server Error", "error");
    } finally {
      setSubmittingByInvoice((prev) => ({ ...prev, [invoiceId]: false }));
    }
  };

  const handleMarkPaid = async (row) => {
    const invoiceId = row.id;
    if ((row.decision || "").toLowerCase() === "paid") {
      return;
    }
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to mark this invoice as paid?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#198754",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, mark paid",
    });
    if (!confirm.isConfirmed) {
      return;
    }
    try {
      setMarkingPaidByInvoice((prev) => ({ ...prev, [invoiceId]: true }));
      const formData = new FormData();
      formData.append("action", "mark_paid");
      formData.append("invoice_id", String(invoiceId));

      const response = await axios.post(`${apiurl}accounts.php`, formData);
      if (response.data?.status === true) {
        Swal.fire("Success", response.data.message || "Marked as paid", "success");
        await fetchApprovedInvoices();
        await fetchPaymentUpdates();
      } else {
        Swal.fire("Error", response.data?.message || "Server Error", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Server Error", "error");
    } finally {
      setMarkingPaidByInvoice((prev) => ({ ...prev, [invoiceId]: false }));
    }
  };

  const handleReceiptUpload = async (row, file) => {
    const invoiceId = row.id;
    const isPaid = (row.decision || "").toLowerCase() === "paid";
    if (!isPaid) {
      Swal.fire("Warning", "Receipt upload is allowed only for paid invoices", "warning");
      return;
    }
    if (!file) return;

    try {
      setReceiptUploadingByInvoice((prev) => ({ ...prev, [invoiceId]: true }));
      const formData = new FormData();
      formData.append("action", "upload_receipt");
      formData.append("invoice_id", String(invoiceId));
      formData.append("receipt", file);

      const response = await axios.post(`${apiurl}accounts.php`, formData);
      if (response.data?.status === true) {
        Swal.fire("Success", response.data.message || "Receipt uploaded successfully", "success");
        await fetchPaymentUpdates();
      } else {
        Swal.fire("Error", response.data?.message || "Server Error", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Server Error", "error");
    } finally {
      setReceiptUploadingByInvoice((prev) => ({ ...prev, [invoiceId]: false }));
    }
  };

  return (
    <div className="main-content app-content">
      <style>{`
        .payment-switch-lg .form-check-input {
          width: 3rem;
          height: 1.6rem;
          cursor: pointer;
        }
        .receipt-upload-disabled {
          background-color: #9ca3af !important;
          border-color: #9ca3af !important;
          color: #ffffff !important;
          opacity: 1 !important;
        }
      `}</style>
      <div className="container-fluid mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="fw-bold mb-0 text-2xl">Accounts</h2>
        </div>

        <div className="d-flex gap-2 mb-3">
          <button
            className={`btn ${activeTab === "approved" ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setActiveTab("approved")}
          >
            Approved Invoices
          </button>
          <button
            className={`btn ${activeTab === "updates" ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setActiveTab("updates")}
          >
            Payment Updates
          </button>
        </div>

        <div className="card custom-card">
          <div className="card-body p-0">
            <div className="table-responsive">
              {activeTab === "approved" ? (
                <table className="table table-bordered mb-0 align-middle">
                  <thead className="table-head-sky">
                    <tr>
                      <th style={{ width: "60px" }}>S.No</th>
                      <th>Invoice No</th>
                      <th>Client Name</th>
                      <th>Net Payable Amount</th>
                      <th>Invoice Date</th>
                      <th>Manager</th>
                      <th style={{ minWidth: "180px" }}>UTR No.</th>
                      <th style={{ minWidth: "220px" }}>Remark</th>
                      <th className="text-center" style={{ width: "140px" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingApproved ? (
                      <tr>
                        <td colSpan="9" className="text-center text-muted py-4">
                          Loading...
                        </td>
                      </tr>
                    ) : approvedRows.length > 0 ? (
                      approvedRows.map((item, index) => (
                        <tr key={item.id}>
                          <td>{index + 1}</td>
                          <td className="fw-semibold">{item.invoice_no || "-"}</td>
                          <td>{item.client_name || "-"}</td>
                          <td>{formatAmount(getAccountsAmount(item))}</td>
                          <td>{formatDate(item.invoice_date)}</td>
                          <td>{item.manager_name || "-"}</td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Enter UTR No."
                              value={formByInvoice[item.id]?.utr_no || ""}
                              onChange={(e) => updateFormField(item.id, "utr_no", e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Enter Remark (optional)"
                              value={formByInvoice[item.id]?.remark || ""}
                              onChange={(e) => updateFormField(item.id, "remark", e.target.value)}
                            />
                          </td>
                          <td className="text-center">
                            <button
                              type="button"
                              className="btn btn-sm btn-success"
                              disabled={submittingByInvoice[item.id] === true}
                              onClick={() => handleSubmitUpdate(item)}
                            >
                              {submittingByInvoice[item.id] ? "Submitting..." : "Submit"}
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center text-muted py-4">
                          No approved invoices found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              ) : (
                <table className="table table-bordered mb-0 align-middle">
                  <thead className="table-head-sky">
                    <tr>
                      <th style={{ width: "60px" }}>S.No</th>
                      <th>Invoice No</th>
                      <th>Client Name</th>
                      <th>Net Payable Amount</th>
                      <th>Invoice Date</th>
                      <th>UTR No.</th>
                      <th>Remark</th>
                      <th>Updated At</th>
                      <th className="text-center" style={{ width: "140px" }}>Mark Paid</th>
                      <th className="text-center" style={{ width: "170px" }}>Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingUpdates ? (
                      <tr>
                        <td colSpan="10" className="text-center text-muted py-4">
                          Loading...
                        </td>
                      </tr>
                    ) : updateRows.length > 0 ? (
                      updateRows.map((item, index) => (
                        <tr key={item.id}>
                          <td>{index + 1}</td>
                          <td className="fw-semibold">{item.invoice_no || "-"}</td>
                          <td>{item.client_name || "-"}</td>
                          <td>{formatAmount(getAccountsAmount(item))}</td>
                          <td>{formatDate(item.invoice_date)}</td>
                          <td>{item.utr_no || "-"}</td>
                          <td>{item.remark || "-"}</td>
                          <td>{formatDateTime(item.updated_at || item.created_at)}</td>
                          <td className="text-center">
                            <div className="form-check form-switch d-inline-flex justify-content-center payment-switch-lg">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                checked={(item.decision || "").toLowerCase() === "paid"}
                                disabled={markingPaidByInvoice[item.id] === true || (item.decision || "").toLowerCase() === "paid"}
                                onChange={() => handleMarkPaid(item)}
                                title={(item.decision || "").toLowerCase() === "paid" ? "Already paid" : "Slide to mark paid"}
                              />
                            </div>
                          </td>
                          <td className="text-center" style={{ width: "170px" }}>
                            {(() => {
                              const isPaid = (item.decision || "").toLowerCase() === "paid";
                              return (
                                <>
                            <input
                              type="file"
                              id={`payment-receipt-${item.id}`}
                              className="d-none"
                              accept=".pdf,.jpg,.jpeg,.png,.zip,.doc,.docx"
                              onChange={(e) => {
                                const file = e.target.files?.[0] || null;
                                handleReceiptUpload(item, file);
                                e.target.value = "";
                              }}
                            />
                            <div className="d-flex gap-2 justify-content-center">
                              <button
                                type="button"
                                className={`btn btn-sm ${isPaid ? "btn-outline-success" : "receipt-upload-disabled"}`}
                                disabled={!isPaid || receiptUploadingByInvoice[item.id] === true}
                                onClick={() => document.getElementById(`payment-receipt-${item.id}`)?.click()}
                              >
                                {receiptUploadingByInvoice[item.id] ? "Uploading..." : "Upload Document"}
                              </button>
                              {item.receipt_document ? (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => openReceiptDocument(item.receipt_document)}
                                >
                                  View
                                </button>
                              ) : null}
                            </div>
                                </>
                              );
                            })()}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="10" className="text-center text-muted py-4">
                          No payment updates found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Accounts;
